import './style.scss';
import { useCompatibilityCheck } from './hooks';
import {
  useCompatibilityStartConfirm,
  CompatibilityCheckConditionType,
} from './questions/questionConfirm/questionStartConfirm/hooks';
import { QuestionStartPage } from './questions/questionStart';
import { QuestionPage } from './questions/question';
import { QuestionCompletePage } from './questions/questionComplete';
import { QuestionConfirmPage, ConfirmType } from './questions/questionConfirm';
import { ImpossibleTransactionPage } from './questions/impossibleTransaction';
import { useEffect } from 'react';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { LoadingContainer, LoadingSize } from 'views/mutual/loading';
import { GetCompatibilityQuery } from 'views/utils/graphql/graphql';
import { ProductType, ProductTypeMap } from 'constants/productType';
import { AccountTabs } from 'views/mutual/tabs/accountTabs';

export type CompatibilityCheckProps = {
  assetType: ProductType;
  completedNextPage?: { label: string; navigate: () => void };
};

// メニュー項目から遷移してくる場合はこちらを表示
export const CompatibilityCheckHome = () => {
  const tabItems = [
    {
      title: ProductTypeMap[ProductType.LeaseFund].name,
      content: <CompatibilityCheck assetType={ProductType.LeaseFund} />,
      productType: ProductType.LeaseFund,
    },
    {
      title: ProductTypeMap[ProductType.DomesticRealEstate].name,
      content: <CompatibilityCheck assetType={ProductType.DomesticRealEstate} />,
      productType: ProductType.DomesticRealEstate,
    },
    {
      title: ProductTypeMap[ProductType.OverseasRealEstate].name,
      content: <CompatibilityCheck assetType={ProductType.OverseasRealEstate} />,
      productType: ProductType.OverseasRealEstate,
    },
  ];

  return (
    <div className="compatibilityCheckHome">
      <AccountTabs tabItems={tabItems} />
    </div>
  );
};

const hasAnyWebAnswerSuccess = (data? : GetCompatibilityQuery) : boolean => {
  return !!(data?.GetCompatibility?.LeaseFundAnswer?.WebAnswer?.Success || 
  data?.GetCompatibility?.DomesticRealEstateAnswer?.CorporateWebAnswer?.Success ||
  data?.GetCompatibility?.DomesticRealEstateAnswer?.PersonalWebAnswer?.Success || 
  data?.GetCompatibility?.OverseasRealEstateAnswer?.WebAnswer?.Success)
}

// 申し込み画面から遷移してくる場合はこちらを表示
export const CompatibilityCheck = (props: CompatibilityCheckProps) => {
  const { checkState, questionToAnswers, start, next, backQuestion, complete, setAnswers } = useCompatibilityCheck(
    props.assetType,
  );
  useCustomLocationName('適合性チェック（ご投資にあたってのご質問）');
  const compatibilityStartConfirm = useCompatibilityStartConfirm(
    setAnswers,
    CompatibilityCheckConditionType.ApprovalRequired,
    props.assetType,
  );
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [checkState]);

  switch (checkState.kind) {
    case 'start':
      const shouldShowStartPage: boolean = !hasAnyWebAnswerSuccess(compatibilityStartConfirm.compatibility.data) || compatibilityStartConfirm.isEditing;

      return (
        <LoadingContainer isLoading={compatibilityStartConfirm.compatibility.fetching} size={LoadingSize.Medium}>
          {shouldShowStartPage ? (
            <QuestionStartPage assetType={props.assetType} onSubmit={start} />
          ) : (
            <QuestionConfirmPage
              {...compatibilityStartConfirm}
              onSubmit={complete}
              questionToAnswers={questionToAnswers}
              confirmType={ConfirmType.ShowOnStart}
            />
          )}
        </LoadingContainer>
      );
    case 'continue':
      return (
        <QuestionPage
          question={checkState.question}
          initialAnswer={checkState.answered === undefined ? null : checkState.answered}
          onSubmit={next}
          backQuestion={backQuestion}
        />
      );
    case 'confirm':
      return (
        <QuestionConfirmPage
          questionToAnswers={questionToAnswers}
          onSubmit={complete}
          backQuestion={backQuestion}
          confirmType={ConfirmType.AfterInput}
        />
      );
    case 'complete':
      return <QuestionCompletePage {...props} />;
    case 'impossible':
      return <ImpossibleTransactionPage />;
  }
};
